/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Mobx model(s) related to bookshelf
 *
 * @file Bookshelf.js
 * @author Saravanan Kandasamy
 */

import { types, applySnapshot, getSnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';

import {
  BOOKSHELF_DATA_REQUESTED,
  BOOKSHELF_DATA_FETCHED,
  REMOVE_ENTITLEMENT_SUCCESS,
  ENTITLEMENTLEVEL_MULTI,
  ACTIVE,
  HERO_BANNER_TYPES,
  DIAGNOSTICS_SERVICE_REQUESTED
} from '../../../common/constants';
import ProductUtils from '../../../common/utils/ProductUtils';
import CommonUtils from '../../../common/utils/CommonUtils';
import Book from './Book';

/**
 * A mobx model for bookshelf
 *
 */
const Bookshelf = types.model(
  'Bookshelf',
  {
    books: types.array(Book)
  }
).views(self => ({
  /**
   * Get all books
   * @returns {Array} - All books
   */
  getBooks() {
    return getSnapshot(self.books);
  },
  /**
   * Get recently accessed books
   * @returns {Array} - Recently accessed books
   */
  getRecentlyAccessedBooks() {
    const books = self.getBooks();
    const activeBooks = CommonUtils.getActiveBooks(books);

    if (!activeBooks?.length) {
      return [];
    }

    return CommonUtils.sortByTimestampDesc(activeBooks, book => book.last_accessed_time);
  },
  /**
   * Get recently created courses
   * @returns {Array} - Recently created courses
   */
  getRecentlyCreatedCourses() {
    const books = self.getBooks();
    const activeBooks = CommonUtils.getActiveBooks(books);

    if (!activeBooks?.length) {
      return [];
    }

    return CommonUtils.sortByTimestampDesc(activeBooks, book => book.course_created_date);
  },
  /**
   * Get newly added non-course books
   * @returns {Object} - Newly added non-course books
   */
  getNewlyAddedBooks() {
    const books = self.getBooks();
    const activeBooks = CommonUtils.getActiveBooks(books);

    // Course books are excluded because the CourseWare book's product_entitlements.start_date is set to the current time on every bookshelf fetch.
    const nonCourseBooks = activeBooks.filter(book => !book.course_id);

    if (!activeBooks?.length) {
      return [];
    }

    return CommonUtils.sortByTimestampDesc(nonCourseBooks, book => book?.product_entitlements?.start_date);
  },
  /**
   * Get book by course id
   * @param {string} courseId - Course ID
   * @returns {Object} - Book
   */
  getBookByCourseId(courseId) {
    const books = self.getBooks();
    return books.find(book => book.course_id === courseId);
  }
})).actions(self => ({
  fetch() {
    Framework.getEventManager().publish(BOOKSHELF_DATA_REQUESTED, {});
  },
  refresh() {
    Framework.getEventManager().publish(BOOKSHELF_DATA_REQUESTED, { refresh: true });
  },
  set(...data) {
    applySnapshot(self, data[0]);
    if(!data[1])
    {
      Framework.getEventManager().publish(BOOKSHELF_DATA_FETCHED, {});
    }
  },
  removeEntitlement(productId, isBanner) {
    const books = self.books.filter(book => book.product_id !== productId);
    if (isBanner) {
      applySnapshot(self.bannerBook, {});
    } else {
      applySnapshot(self.books, books);
    }
    Framework.getEventManager().publish(REMOVE_ENTITLEMENT_SUCCESS);
  },
  /**
   * Mapping subscription details to book and setting product model name
   *
   */
  setSubscriptionDetails() {
    const userStore = Framework.getStoreRegistry().getStore('user');
    const user = getSnapshot(userStore);
    const books = getSnapshot(self.books).map((book) => {
      let renderBook = {};
      let gpsSubscriptionOfCurrentBook = null;
      let entitlementTierOfBook = null;
      let entitlementLevelOfBook = null;
      let subscriptionStatusOfBook = null;
      const { subscription_id: subscriptionIdOfBook } = CommonUtils.getProductEntitlementOfBook(book);
      if (subscriptionIdOfBook) {
        const {
          entitlementTier,
          status,
          entitlementLevel,
          subscriptionSource
        } = CommonUtils.getMatchingGpsSubscriptionOfBook(subscriptionIdOfBook, user);
        entitlementTierOfBook = entitlementTier;
        entitlementLevelOfBook = entitlementLevel;
        subscriptionStatusOfBook = status;
        gpsSubscriptionOfCurrentBook = {};
        gpsSubscriptionOfCurrentBook.status = status || null;
        gpsSubscriptionOfCurrentBook.entitlementLevel = entitlementLevel || null;
        gpsSubscriptionOfCurrentBook.subscriptionSource = subscriptionSource || null;
      }
      const productModelName = ProductUtils.getProductModel(book.product_entitlements.business_model_code, entitlementTierOfBook, false);
      const format = ProductUtils.getProductTypeFormat(book.book_type);
      renderBook = {
        ...book,
        product_model_name: `${productModelName}, ${format}`,
        gpsSubscriptionOfBook: gpsSubscriptionOfCurrentBook
      };
      const isActiveBook = CommonUtils.isActiveBook(renderBook);
      return {
        ...renderBook,
        showAudio: book.showAudio,
        showBookmark: true,
        showFlashcard: true,
        showRemoveEntitlement: ((entitlementLevelOfBook === ENTITLEMENTLEVEL_MULTI)
          && (subscriptionStatusOfBook === ACTIVE) && isActiveBook)
      };
    });

    applySnapshot(self.books, books);
  },
  /**
   * Archives or unarchives selected books
   *
   * @param {Array} selectedBookIds
   * @param {*} isArchived
   */
  archiveOrUnarchiveSelectedBooks(selectedBookIds = [], isArchived = false) {
    let heroBookId = null;
    const heroBannerStore = Framework.getStoreRegistry().getStore('herobanner');
    const { titleInfo } = getSnapshot(heroBannerStore);
    const books = getSnapshot(self)?.books;
    const updatedBooks = books.map((book) => {
      if (titleInfo?.book_id === book.book_id
        && titleInfo?.product_id === book.product_id
        && titleInfo?.course_id === book.course_id) {
        heroBookId = book.book_id;
      }
      if (selectedBookIds.includes(book.book_id) && book.isArchived !== isArchived) {
        return {
          ...book,
          isArchived
        };
      }

      return { ...book };
    });

    if (updatedBooks.length > 0) {
      applySnapshot(self.books, updatedBooks);
      const { BOOK } = HERO_BANNER_TYPES;
      const fetchChannels = selectedBookIds.includes(heroBookId) || heroBannerStore.bannerType !== BOOK;
      this.setShowArchiveOrUnarchive(true);
    }
  },
  setShowArchiveOrUnarchive(enableTitleArchival) {
    const { books } = getSnapshot(self);

    const updatedBooks = books.map(book => ({
      ...book,
      showArchive: enableTitleArchival && !book.isArchived,
      showUnArchive: enableTitleArchival && book.isArchived
    }));
    applySnapshot(self.books, updatedBooks);
  },
  fetchDiagnosticsInfo(data) {
    const eventData = {
      eventType: 'GET',
      data
    }
    Framework.getEventManager().publish(DIAGNOSTICS_SERVICE_REQUESTED, eventData);
  },
  setDiagnosticsInfo(diagnosticsInfo, courseRegistrarId) {
    const { books } = getSnapshot(self);
    const updatedBooks = books.map(book => ({
      ...book,
      diagnosticsInfo:  CommonUtils.handleLMSBookMatchByCourseId(book, courseRegistrarId)
        ? diagnosticsInfo
        : null
    }));
    applySnapshot(self.books, updatedBooks);
  },
  setShowDiagnosticsLink(courseRegistrarId) {
    const { books } = getSnapshot(self);
    const updatedBooks = books.map(book => ({
      ...book,
      showRunDiagnostics: CommonUtils.handleLMSBookMatchByCourseId(book, courseRegistrarId)
    }));
    const hasChanges = updatedBooks.some((book, index) => (
      book.showRunDiagnostics !== books[index]?.showRunDiagnostics
    ));

    if (!hasChanges) {
      return;
    }
    applySnapshot(self.books, updatedBooks);
  },
  // eslint-disable-next-line camelcase
  setLastAccessBook({ book_id, course_id, product_id }) {
    const { books } = getSnapshot(self);

    const renderBooks = books.map((book) => {
      const renderBook = { ...book };
      // eslint-disable-next-line camelcase
      if (book.book_id === book_id && book.course_id === course_id && book.product_id === product_id) {
        renderBook.last_accessed_time = new Date().getTime();
      }

      return renderBook;
    });

    applySnapshot(self.books, renderBooks);
  },
  setError() {
  },
  /**
   * Set user book preference
   *
   * @param {Object} userBookPreference - User book preference
   * @param {string} courseId - Course ID
   * @param {string} bookId - Book ID
   * @param {string} productId - Product ID
   * @returns {void}
   */
  setUserBookPreference(userBookPreference, courseId, bookId, productId) {
    if (!userBookPreference || CommonUtils.isEmpty(userBookPreference) || self?.books?.length === 0) {
      return;
    }

    const currentBook = self.books.find(book => (
      book.book_id === bookId
        && book.product_id === productId
        && book.course_id === courseId
    ));

    if (!currentBook) {
      return;
    }

    currentBook.userBookPreference = userBookPreference;
  },
  /**
   * Set course configuration
   *
   * @param {Object} data - Course configuration data
   * @param {string} courseId - Course ID
   * @returns {void}
   */
  setCourseConfiguration(data = {}, courseId) {
    const { configurations } = data;
    if (!courseId || !configurations || self?.books?.length === 0) {
      return;
    }

    const currentBook = self.books.find(book => book.course_id === courseId);

    if (!currentBook) {
      return;
    }

    currentBook.courseConfiguration = configurations;
  },
  /**
   * Set course configuration
   *
   * @param {Object} data - Course configuration data
   * @param {string} courseId - Course ID
   * @returns {void}
   */
  setCourseSettings(data = {}, courseId) {
    const { settings } = data;

    if (!courseId || !settings || self?.books?.length === 0) {
      return;
    }

    const currentBook = self.books.find(book => book.course_id === courseId);

    if (!currentBook) {
      return;
    }

    currentBook.courseSettings = settings;
  },
  /**
   * Set instructor controls from API configurations
   *
   * @param {Array} configurations - Instructor configuration entries
   * @returns {void}
   */
  setInstructorControls(configurations = []) {
    configurations.forEach(({ courseId, controls }) => {
      const book = self.books.find(b => b.course_id === courseId);

      if (book) {
        const isTACourse = CommonUtils.isTACourse(book);

        book.controls = {
          ...controls,
          createCourse: isTACourse ? false : controls.createCourse,
          createCopyCourse: isTACourse ? false : controls.createCopyCourse
        };
      }
    });
  },
  /**
   * Updates the location for the book
   *
   * @param {Object} book
   * @returns {void}
   */
  updateLocation(book = {}) {
    const books = self.books;

    if (!books?.length) {
      return;
    }

    const currentBook = books.find(
      b => b.course_id === book.course_id && b.book_id === book.book_id && b.product_id === book.product_id
    );

    if (!currentBook) {
      return;
    }

    currentBook.last_accessed_time = new Date().getTime();
  }
}));

export default Bookshelf;
